import { apiUrl, envConfig, getCurrentEnv } from '../config/envConfig';
import { getEnvironmentParams } from './auth';

// State for the (possibly multi-authoriser) consent flow.
//
// `authSession` (localStorage) is the source of truth and holds the full list of
// authorisations plus the index of the one currently being driven. `consentData`
// (localStorage) is kept as a *mirror* of the active authorisation so the existing
// downstream screens (BankConnection / BankConnected / PickAccount) keep working
// without needing to know about the array. Only this module writes the mirror.

export const AUTH_SESSION_KEY = 'authSession';
export const CONSENT_DATA_KEY = 'consentData';

// scaStatus / consent status values that mean the authorisation can no longer succeed.
const TERMINAL_FAILURE = [
    'failed', 'rejected', 'denied', 'aborted', 'cancelled',
    'expired', 'terminated', 'blocked', 'invalid', 'revoked',
];

export const getAuthSession = () => {
    try {
        return JSON.parse(localStorage.getItem(AUTH_SESSION_KEY)) || null;
    } catch {
        return null;
    }
};

export const saveAuthSession = (session) => {
    localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
    return session;
};

export const clearAuthSession = () => {
    localStorage.removeItem(AUTH_SESSION_KEY);
    localStorage.removeItem(CONSENT_DATA_KEY);
    // Also clear the sessionStorage compatibility mirror (see syncActiveAuthorization).
    try { sessionStorage.removeItem(CONSENT_DATA_KEY); } catch { /* no-op */ }
};

// Mirror the currently-active authorisation into `consentData` for downstream screens.
export const syncActiveAuthorization = (session) => {
    if (!session || !Array.isArray(session.list) || session.list.length === 0) {
        return session;
    }
    const el = session.list[session.currentIndex] || session.list[0];
    const mirror = {
        consentId: session.consentId,
        authorizationId: el.authorizationId,
        authoriserId: el.authoriserId ?? null,
        challengeData: el.challengeData ?? null,
        scaOAuthLink: el.scaOAuthLink ?? null,
        scaStatus: el.scaStatus ?? null,
    };
    const serialized = JSON.stringify(mirror);
    // localStorage is the source of truth (survives reload/redirect/email cold-start).
    localStorage.setItem(CONSENT_DATA_KEY, serialized);
    // sessionStorage mirror keeps the not-yet-migrated readers (PickAccount, and the
    // legacy fallbacks in BankConnection/BankConnected) working during the migration.
    try { sessionStorage.setItem(CONSENT_DATA_KEY, serialized); } catch { /* no-op */ }
    return session;
};

// Merge a patch into the active element, persist, and re-sync the mirror.
export const updateActiveElement = (patch) => {
    const session = getAuthSession();
    if (!session || !Array.isArray(session.list) || session.list.length === 0) {
        return null;
    }
    const idx = session.currentIndex ?? 0;
    session.list[idx] = { ...session.list[idx], ...patch };
    saveAuthSession(session);
    syncActiveAuthorization(session);
    return session;
};

// First authoriser still needing in-browser signing: not handled out-of-band by email
// and not yet finalised. Returns -1 when every in-browser authoriser is done.
export const nextUnsignedIndex = (session) => {
    if (!session || !Array.isArray(session.list)) {
        return -1;
    }
    return session.list.findIndex(
        (el) => !el.emailChosen
            && !el.emailNotification
            && String(el.scaStatus || '').toLowerCase() !== 'finalised',
    );
};

export const isTerminalFailure = (status) =>
    TERMINAL_FAILURE.includes(String(status || '').toLowerCase());

// Same-device handoff: send the browser to the bank's OAuth URL. The bank returns the user
// to /bank-connected?code=… where the authorisation is activated. Isolated here so it can be
// stubbed in tests (jsdom can't navigate).
export const redirectToBank = (url) => {
    if (url) {
        window.location.assign(url);
    }
};

const fetchUserIp = async () => {
    try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        return data.ip;
    } catch (err) {
        console.error('Error fetching IP address:', err);
        return '127.0.0.1';
    }
};

// Headers for POST /consents — mirrors AspspsList.handleNextClick.
// `force` maps to the header (= ForceConsent on the backend): true forces
// a brand-new consent (first creation); false lets the backend reuse/rehydrate.
export const buildConsentHeaders = ({ bicFi, psuId, userIp, userAgent, aspspsId, force }) => {
    const params = getEnvironmentParams();
    if (!params.token) {
        throw new Error('No authentication token found');
    }
    const headers = {
        'Content-Type': 'application/json',
        'BicFi': bicFi || 'ESSESESS',
        'Psu-Id': psuId,
        'Psu-Ip-Address': userIp,
        'Psu-User-Agent': userAgent,
        'IsSameDevice': force ? 'true' : 'false',
        'Redirect-Uri': `${envConfig[getCurrentEnv()].redirectUri}`,
        'Authorization': `Bearer ${params.token}`,
        'Tpp-Redirect-Preferred': true,
    };
    if (aspspsId) {
        headers['AspspsId'] = aspspsId;
    }
    return headers;
};

// Call POST /consents (StartConsentOrchestration). The backend contract is
// ApiStartConsentAuthResponseDto[] (one element per authoriser) — fail fast on
// anything else rather than coercing it.
//   createInputs = { bicFi, ssn, authoriserIds, aspspsId }
//   force        = false on first attempt; true on retry only if errors occur (forces new consent).
export const startOrchestration = async ({ createInputs, force = false }) => {
    const { bicFi, ssn, authoriserIds, aspspsId } = createInputs;
    const userAgent = navigator.userAgent;
    const userIp = await fetchUserIp();
    const headers = buildConsentHeaders({ bicFi, psuId: ssn, userIp, userAgent, aspspsId, force });

    const response = await fetch(`${apiUrl}/consents`, {
        method: 'POST',
        headers,
        body: JSON.stringify(authoriserIds ?? null),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (!Array.isArray(data)) {
        throw new Error('Unexpected POST /consents response: expected an array of authorisations');
    }
    return data;
};

// Call POST /consents/{consentId}/authorization (InitiateAuthorisation) to re-issue an
// authorisation against the user's chosen SCA method. The backend contract is
// ApiStartConsentAuthResponseDto[]. The body matches InitiateAuthorizationDto
// { authorizationIds, authMethod } (ASP.NET binds the keys case-insensitively).
// NOTE: the response does NOT carry scaMethods again — keep the cached ones.
export const initiateAuthorization = async ({ consentId, authorizationIds, authMethod, createInputs, force = true }) => {
    const { bicFi, ssn, aspspsId } = createInputs;
    const userAgent = navigator.userAgent;
    const userIp = await fetchUserIp();
    const headers = buildConsentHeaders({ bicFi, psuId: ssn, userIp, userAgent, aspspsId, force });

    const response = await fetch(`${apiUrl}/consents/${consentId}/authorization`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ authorizationIds, authMethod }),
    });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (!Array.isArray(data)) {
        throw new Error('Unexpected POST /consents/{id}/authorization response: expected an array of authorisations');
    }
    return data;
};

// GET /consents/{consentId} — the backend contract is a bare JSON string
// (GetConsentStatusQuery : IRequest<HandlerResult<string>>). Returned lower-cased.
export const getConsentStatus = async ({ consentId, createInputs }) => {
    const { bicFi, ssn, aspspsId } = createInputs || {};
    const userAgent = navigator.userAgent;
    const userIp = await fetchUserIp();
    const headers = buildConsentHeaders({ bicFi, psuId: ssn, userIp, userAgent, aspspsId, force: false });

    const response = await fetch(`${apiUrl}/consents/${consentId}`, { method: 'GET', headers });
    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    if (typeof data !== 'string') {
        throw new Error('Unexpected GET /consents/{id} response: expected a status string');
    }
    return data.toLowerCase();
};

// Build (and persist) a fresh authSession from a POST /consents response array.
// scaMethods are cached here because POST /authorization never returns them again.
export const buildAuthSession = (responseArray, createInputs, { isDual } = {}) => {
    const list = (responseArray || []).map((r) => ({
        authorizationId: r.authorizationId,
        authoriserId: r.authoriserId ?? null,
        scaStatus: r.scaStatus ?? null,
        emailAvailable: Boolean(createInputs?.authoriserIds?.[r.authoriserId]) || Boolean(r.emailNotification),
        emailChosen: false,
        emailNotification: r.emailNotification ?? null,
        chosenMethod: null,
        scaMethods: r.scaMethods ?? [],
        challengeData: r.challengeData ?? null,
        scaOAuthLink: r.scaOAuthLink ?? null,
    }));
    const session = {
        consentId: responseArray?.[0]?.consentId ?? null,
        currentIndex: 0,
        isDual: Boolean(isDual),
        createInputs,
        list,
    };

    saveAuthSession(session);
    syncActiveAuthorization(session);
    return session;
};
